import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Clock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { storeInfo } from '../data/mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setStatus('loading');
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, honeypot })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Subscription failed');
      }
      
      const data = await response.json();
      setStatus('success');
      setMessage(data.message || 'Subscribed!');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-teal-50 to-cyan-100 border-t border-teal-200 mt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About Section */}
          <div>
            <h3 className="text-xl font-serif font-bold text-teal-800 mb-4">
              J.B.F. Antiques & Gallery
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              Discover timeless treasures at Doncaster Corn Exchange. 
              We specialize in quality antiques, from Victorian furniture to Art Deco collectibles.
            </p>
            <div className="flex gap-3">
              <a
                href={storeInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-teal-700 text-white flex items-center justify-center hover:bg-teal-600 transition-colors"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-serif font-bold text-teal-800 mb-4">
              Quick Links
            </h3>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="text-gray-700 hover:text-teal-700 transition-colors text-sm">
                Home
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-teal-700 transition-colors text-sm">
                About Us
              </Link>
              <Link to="/gallery" className="text-gray-700 hover:text-teal-700 transition-colors text-sm">
                Gallery
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-teal-700 transition-colors text-sm">
                Contact
              </Link>
              <Link to="/directions" className="text-gray-700 hover:text-teal-700 transition-colors text-sm">
                Directions
              </Link>
            </nav>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-serif font-bold text-teal-800 mb-4">
              Contact Us
            </h3>
            <div className="flex flex-col gap-3 text-sm">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-teal-700 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">
                  {storeInfo.address}
                </span>
              </div>
              <a href={`tel:${storeInfo.phone}`} className="flex items-center gap-3 text-gray-700 hover:text-teal-700 transition-colors">
                <Phone size={18} className="text-teal-700 flex-shrink-0" />
                <span>{storeInfo.phone}</span>
              </a>
              <a href={`mailto:${storeInfo.email}`} className="flex items-center gap-3 text-gray-700 hover:text-teal-700 transition-colors">
                <Mail size={18} className="text-teal-700 flex-shrink-0" />
                <span>{storeInfo.email}</span>
              </a>
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-teal-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-700 font-medium">Opening Hours:</p>
                  <p className="text-gray-600">{storeInfo.openingHours.days}</p>
                  <p className="text-gray-600">{storeInfo.openingHours.hours}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-serif font-bold text-teal-800 mb-4">
              Newsletter
            </h3>
            <p className="text-gray-700 text-sm mb-4">
              Get updates on new arrivals and special offers.
            </p>
            
            {status === 'success' ? (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>{message}</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="space-y-2" data-testid="footer-newsletter-form">
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white border-teal-200 text-gray-800 placeholder:text-gray-400 text-sm"
                    required
                    disabled={status === 'loading'}
                    data-testid="footer-newsletter-email"
                  />
                  <Button 
                    type="submit" 
                    className="bg-teal-700 hover:bg-teal-600 px-3"
                    disabled={status === 'loading'}
                    data-testid="footer-newsletter-submit"
                  >
                    {status === 'loading' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {status === 'error' && (
                  <p className="text-red-600 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {message}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-teal-200 mt-8 pt-6 text-center">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} J.B.F. Antiques & Gallery. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
